/**
 * The single inline cursor: one rendered-order projection of a block's text,
 * shared by every recognizer and every edit that follows one.
 *
 * Emoji scanning, marker recognition, marker consumption, and lead-in
 * recognition each walking the tree themselves diverge on split leaves and
 * nested emphasis. One projection with many consumers is the only shape in
 * which a recognizer and the splice acting on its result cannot disagree about
 * where a token sits.
 */
import {
    classifyEditorialCapabilities,
    type EditorialHastNode,
} from "./editorial-capabilities";

type HastNodeLike = EditorialHastNode;

export interface EditorialLeafPosition {
    readonly leaf: HastNodeLike;
    readonly offset: number;
}

export interface EditorialLeafRange {
    readonly leaf: HastNodeLike;
    readonly start: number;
    readonly end: number;
}

export interface EditorialInlineRun {
    /** Rendered inline text of the block; opaque subtrees contribute nothing. */
    readonly text: string;
    /**
     * Offset of the first non-whitespace CHARACTER, or undefined if blank.
     * Text-only by construction: content that renders without contributing
     * text (an anchor label, an image) does not move it. Ask `isLeadingToken`
     * whether a token actually leads the block — it accounts for both.
     */
    readonly firstMeaningfulOffset: number | undefined;
    /**
     * Every addressable text node, in rendered order. This is the definition of
     * "spliceable within this block": anything behind an opaque boundary or
     * inside a nested block is absent, so a validator can consult this instead
     * of restating the descent rule and drifting from it.
     */
    readonly leaves: readonly HastNodeLike[];
    readonly resolve: (offset: number) => EditorialLeafPosition | undefined;
    readonly sliceToLeafRanges: (
        start: number,
        end: number
    ) => readonly EditorialLeafRange[];
    /** Whether `[start, end)` is the block's complete first semantic token. */
    readonly isLeadingToken: (start: number, end: number) => boolean;
}

interface RunLeaf {
    readonly node: HastNodeLike;
    readonly value: string;
    readonly start: number;
}

interface RunProjection {
    readonly leaves: readonly RunLeaf[];
    /**
     * Text offsets at which something renders without contributing text — an
     * opaque subtree, an image, a line break. Never addressable, but present:
     * a marker cannot lead a block that renders content before it.
     */
    readonly occupancies: readonly number[];
    /**
     * Offsets where an inline wrapper that rendered text opens or closes — the
     * only boundaries a reader can perceive. Deliberately NOT every leaf start:
     * a comment, an empty wrapper or two adjacent text nodes split the leaves
     * while rendering nothing between them.
     */
    readonly boundaries: ReadonlySet<number>;
}

// Any whitespace, newlines included. The grammar says "horizontal whitespace",
// but remark keeps a markdown soft line break as a literal "\n" in the text
// node and HTML renders it as a space — so excluding it would decline a marker
// the reader sees as separated. Rendered semantics decide the boundary.
const SEPARATING_WHITESPACE = /\s/;

// Elements the run walks THROUGH. An allowlist rather than a block-tag denial
// list: an unknown or custom element then renders atomically instead of having
// its text fused into this block's run, which is the safe direction.
//
// The block-scan axis cannot answer this. Its `transparent` is the classifier's
// fallback for every unrecognized element — `ul`, `div`, `h2` all land there —
// because descending through a `ul` is correct when DISCOVERING blocks. The
// inline projection is asking a different question of the same node.
const INLINE_DESCENT_TAGS: ReadonlySet<string> = new Set([
    "em",
    "strong",
    "span",
    "b",
    "i",
    "u",
    "s",
    "strike",
    "del",
    "ins",
    "mark",
    "small",
    "sub",
    "sup",
    "abbr",
    "q",
    "cite",
    "dfn",
    "var",
    "kbd",
    "samp",
    "time",
    "bdi",
    "bdo",
    "ruby",
    "rt",
    "rp",
]);

// Separate blocks: they contribute no text AND no occupancy, because a nested
// list under a list item is not content rendered "inside" that item's line.
const BLOCK_TAGS: ReadonlySet<string> = new Set([
    "p",
    "div",
    "ul",
    "ol",
    "li",
    "dl",
    "dt",
    "dd",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "figure",
    "figcaption",
    "section",
    "article",
    "aside",
    "header",
    "footer",
    "main",
    "nav",
    "form",
    "fieldset",
    "address",
    "details",
    "summary",
]);

/**
 * Whether a tag is an inline wrapper the layer may see through. Exported
 * because "which elements are formatting rather than structure" is one
 * taxonomy: the container-outcome pass prunes exactly the wrappers this walk
 * descends into, and a second copy of the list would drift from this one.
 */
export const isInlineWrapperTag = (tagName: string): boolean =>
    INLINE_DESCENT_TAGS.has(tagName);

/**
 * Nothing the reader sees. Exported for the same reason `isInlineWrapperTag`
 * is: what counts as ignorable has to stay identical across the inline cursor,
 * the block descent and the adjacency scan, or the ordering contract between
 * them holds only by coincidence.
 */
export const isIgnorableNode = (node: HastNodeLike): boolean =>
    node.type === "comment" ||
    (node.type === "text" && (node.value ?? "").trim() === "");

/**
 * The first child that occupies the reader's attention.
 *
 * Whitespace-only text and comments are skipped because neither is something
 * the reader sees; anything else counts, so a paragraph opening with a word,
 * an image, or a link is not marker-led even when a marker follows.
 */
export const firstMeaningfulChild = (
    node: HastNodeLike
): HastNodeLike | undefined =>
    (node.children ?? []).find((child) => !isIgnorableNode(child));

/**
 * Loose list items wrap their leading text in a `p`; tight ones do not. Both
 * must project to the same stream, so the wrapper is unwrapped here at the
 * root rather than special-cased mid-walk. Only the LEADING paragraph is
 * unwrapped — a second `p`, or a nested list beside it, is its own block.
 */
const resolveRunRoot = (block: HastNodeLike): HastNodeLike => {
    if ((block.tagName ?? "") !== "li") return block;

    const first = firstMeaningfulChild(block);
    return first?.type === "element" && first.tagName === "p" ? first : block;
};

/**
 * The root's own capability is deliberately not consulted — the caller names
 * the block. Table cells are block-scan opaque yet still receive inline
 * display typesetting through the adapters, so refusing an opaque root here
 * would silently empty every cell run.
 */
const project = (block: HastNodeLike): RunProjection => {
    const leaves: RunLeaf[] = [];
    const occupancies: number[] = [];
    const boundaries = new Set<number>();
    let cursor = 0;
    type Frame =
        | { readonly kind: "node"; readonly node: HastNodeLike }
        | { readonly kind: "close"; readonly opened: number };
    const root = resolveRunRoot(block);
    const stack: Frame[] = [];
    const seen = new Set<unknown>([root]);

    const pushChildren = (parent: HastNodeLike): void => {
        const children = parent.children ?? [];
        for (let index = children.length - 1; index >= 0; index--) {
            stack.push({ kind: "node", node: children[index] });
        }
    };

    pushChildren(root);
    while (stack.length > 0) {
        const frame = stack.pop() as Frame;
        if (frame.kind === "close") {
            if (cursor > frame.opened) {
                boundaries.add(frame.opened);
                boundaries.add(cursor);
            }
            continue;
        }

        const child = frame.node;
        if (seen.has(child)) continue;
        seen.add(child);

        if (child.type === "text") {
            const value = child.value ?? "";
            if (value === "") continue;
            leaves.push({ node: child, value, start: cursor });
            cursor += value.length;
            continue;
        }
        if (child.type !== "element") continue;

        if (classifyEditorialCapabilities(child).blockScan !== "transparent") {
            occupancies.push(cursor);
            continue;
        }

        const tagName = child.tagName ?? "";
        if (BLOCK_TAGS.has(tagName)) continue;
        if (INLINE_DESCENT_TAGS.has(tagName)) {
            stack.push({ kind: "close", opened: cursor });
            pushChildren(child);
            continue;
        }
        occupancies.push(cursor);
    }
    return { leaves, occupancies, boundaries };
};

export const buildInlineRun = (block: HastNodeLike): EditorialInlineRun => {
    const { leaves, occupancies, boundaries } = project(block);
    const text = leaves.map((leaf) => leaf.value).join("");

    const firstNonBlank = text.search(/\S/);
    const firstMeaningfulOffset =
        firstNonBlank === -1 ? undefined : firstNonBlank;

    const resolve = (offset: number): EditorialLeafPosition | undefined => {
        if (offset < 0 || offset >= text.length) return undefined;

        for (const leaf of leaves) {
            if (offset < leaf.start + leaf.value.length) {
                return { leaf: leaf.node, offset: offset - leaf.start };
            }
        }
        return undefined;
    };

    const sliceToLeafRanges = (
        start: number,
        end: number
    ): readonly EditorialLeafRange[] => {
        // `from >= to` is false for NaN, so without this the clamp emits a
        // NaN-bounded range instead of nothing and the projection stops being
        // total in fact rather than only in intent.
        if (!Number.isFinite(start) || !Number.isFinite(end)) return [];

        const from = Math.max(0, start);
        const to = Math.min(text.length, end);
        if (from >= to) return [];

        const ranges: EditorialLeafRange[] = [];
        for (const leaf of leaves) {
            const leafEnd = leaf.start + leaf.value.length;
            if (leafEnd <= from) continue;
            if (leaf.start >= to) break;

            ranges.push({
                leaf: leaf.node,
                start: Math.max(from, leaf.start) - leaf.start,
                end: Math.min(to, leafEnd) - leaf.start,
            });
        }
        return ranges;
    };

    // Ascending by construction, so the ends are the only entries worth testing.
    const rendersBefore = (offset: number): boolean =>
        occupancies.length > 0 && occupancies[0] <= offset;
    const rendersFrom = (offset: number): boolean =>
        occupancies.length > 0 && occupancies[occupancies.length - 1] >= offset;

    const isTokenBoundary = (offset: number): boolean =>
        (offset === text.length && !rendersFrom(offset)) ||
        boundaries.has(offset) ||
        SEPARATING_WHITESPACE.test(text.charAt(offset));

    // Composed rather than exposed as separate predicates: a consumer that
    // checked only the trailing boundary would accept a marker buried
    // mid-paragraph, and one that checked only text would accept a marker
    // trailing an image.
    const isLeadingToken = (start: number, end: number): boolean =>
        firstMeaningfulOffset !== undefined &&
        start === firstMeaningfulOffset &&
        !rendersBefore(start) &&
        end > start &&
        end <= text.length &&
        isTokenBoundary(end);

    return {
        text,
        firstMeaningfulOffset,
        leaves: leaves.map((leaf) => leaf.node),
        resolve,
        sliceToLeafRanges,
        isLeadingToken,
    };
};
